import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Globe, Instagram, Facebook, Youtube } from "lucide-react";
import { getAuthor } from "@/lib/api";
import BookCard from "@/components/BookCard";
import Seo from "@/components/Seo";

export default function AuthorDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    getAuthor(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl text-[#0f2544]">Autor no encontrado</h1>
        <Link to="/autores" className="hv-link-underline text-[#1f44e0] mt-4 inline-block">← Volver a autores</Link>
      </div>
    );
  if (!data) return <div className="max-w-5xl mx-auto px-6 py-32 text-[#8a8a83]">Cargando...</div>;

  const { author, books } = data;
  const socials = [
    { url: author.web, icon: Globe, label: "Web" },
    { url: author.instagram, icon: Instagram, label: "Instagram" },
    { url: author.facebook, icon: Facebook, label: "Facebook" },
    { url: author.youtube, icon: Youtube, label: "YouTube" },
  ].filter((s) => s.url);

  return (
    <>
      <Seo
        title={author.seo_title || author.nombre}
        description={author.seo_description || author.biografia_corta}
        jsonLd={{ "@context": "https://schema.org", "@type": "Person", name: author.nombre, image: author.foto || undefined }}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 pt-10">
        <Link to="/autores" className="inline-flex items-center gap-2 text-sm text-[#8a8a83] hover:text-[#1f44e0] transition-colors" data-testid="back-to-authors">
          <ArrowLeft size={16} /> Autores
        </Link>
      </div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14 grid md:grid-cols-[260px_1fr] gap-10">
        <div className="mx-auto w-full max-w-[260px]">
          <div className="aspect-[4/5] rounded-md overflow-hidden bg-[#e7e5df]">
            {author.foto ? <img src={author.foto} alt={author.nombre} className="w-full h-full object-cover" /> : null}
          </div>
          {socials.length > 0 && (
            <div className="flex gap-2 mt-5 justify-center md:justify-start">
              {socials.map((s) => (
                <a key={s.label} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label} className="p-2.5 rounded-full border border-[#0f2544]/15 text-[#4a4a45] hover:border-[#1f44e0] hover:text-[#1f44e0] transition-colors">
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="hv-overline">Autor</p>
          <h1 className="font-serif text-3xl md:text-5xl text-[#0f2544] mt-3" data-testid="author-name">{author.nombre}</h1>
          <div className="mt-6 text-[#4a4a45] leading-relaxed whitespace-pre-line">
            {author.biografia_completa || author.biografia_corta || "Biografía disponible próximamente."}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pb-20">
        <h2 className="font-serif text-2xl md:text-3xl text-[#0f2544] mb-10">Libros de {author.nombre}</h2>
        {books.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8" data-testid="author-books">
            {books.map((b) => (
              <BookCard key={b.slug} book={b} />
            ))}
          </div>
        ) : (
          <p className="text-[#8a8a83]">Este autor todavía no tiene libros publicados en el catálogo.</p>
        )}
      </section>
    </>
  );
}
